/**
 * Used to track the app initialization state of the MetaOS App hosted
 */
export var AppInitState;
(function (AppInitState) {
    AppInitState["Unknown"] = "unknown";
    /**
     * Set when beginning the loading process, before setting the iframe URL
     */
    AppInitState["Loading"] = "loading";
    /**
     * Set when  we have "loaded" the App - set the webview src or sent it the load signal
     */
    AppInitState["WaitingForAppLoad"] = "WaitingForAppLoad";
    /**
     * Used to hide the Hub loading UI
     * IF the native loading indicator is being used, set in response to appInitialization.appLoaded and appInitialization.success
     * Otherwise, set immediately after creating the iFrame
     */
    AppInitState["Loaded"] = "loaded";
    /**
     * Used to show the Hub error UI
     * IF the native loading indicator is being used, set in response to appInitialization.failure
     * or if the appInitialization.success wasn't received after 30s
     * Otherwise, set if no Initialize message received after 60s
     */
    AppInitState["Failed"] = "failed";
    /**
     * Applies to Hubs that opt into Caching
     * Only applicable if Hub passes CacheManagementService implementation and "shouldTriggerUnload" returns true
     * Indicates that the SDK has sent the beforeUnload event and is waiting for the readyToUnload event from App
     */
    AppInitState["Unloading"] = "unloading";
    /**
     * Applies to Hubs that opt into Caching
     * This indicates that the Hub has received the "readyToUnload" event from the App
     */
    AppInitState["Unloaded"] = "unloaded";
    /**
     * Applies to Hubs that opt into Caching
     * This indicates that the App has transitioned to the Cached state
     */
    AppInitState["Cached"] = "cached";
})(AppInitState || (AppInitState = {}));
export var ErrorCode;
(function (ErrorCode) {
    /**
     * API not supported in the current platform.
     */
    ErrorCode[ErrorCode["NOT_SUPPORTED_ON_PLATFORM"] = 100] = "NOT_SUPPORTED_ON_PLATFORM";
    /**
     * Internal error encountered while performing the required operation.
     */
    ErrorCode[ErrorCode["INTERNAL_ERROR"] = 500] = "INTERNAL_ERROR";
    /**
     * API is not supported in current context
     */
    ErrorCode[ErrorCode["NOT_SUPPORTED_IN_CURRENT_CONTEXT"] = 501] = "NOT_SUPPORTED_IN_CURRENT_CONTEXT";
    /**
    Permissions denied by user
    */
    ErrorCode[ErrorCode["PERMISSION_DENIED"] = 1000] = "PERMISSION_DENIED";
    /**
     * Network issue
     */
    ErrorCode[ErrorCode["NETWORK_ERROR"] = 2000] = "NETWORK_ERROR";
    /**
     * Underlying hardware doesn't support the capability
     */
    ErrorCode[ErrorCode["NO_HW_SUPPORT"] = 3000] = "NO_HW_SUPPORT";
    /**
     * One or more arguments are invalid
     */
    ErrorCode[ErrorCode["INVALID_ARGUMENTS"] = 4000] = "INVALID_ARGUMENTS";
    /**
     * User is not authorized for this operation
     */
    ErrorCode[ErrorCode["UNAUTHORIZED_USER_OPERATION"] = 5000] = "UNAUTHORIZED_USER_OPERATION";
    /**
     * Could not complete the operation due to insufficient resources
     */
    ErrorCode[ErrorCode["INSUFFICIENT_RESOURCES"] = 6000] = "INSUFFICIENT_RESOURCES";
    /**
     * Platform throttled the request because of API was invoked too frequently
     */
    ErrorCode[ErrorCode["THROTTLE"] = 7000] = "THROTTLE";
    /**
     * User aborted the operation
     */
    ErrorCode[ErrorCode["USER_ABORT"] = 8000] = "USER_ABORT";
    /**
     * Could not complete the operation in the given time interval
     */
    ErrorCode[ErrorCode["OPERATION_TIMED_OUT"] = 8001] = "OPERATION_TIMED_OUT";
    /**
     * Platform code is old and doesn't implement this API
     */
    ErrorCode[ErrorCode["OLD_PLATFORM"] = 9000] = "OLD_PLATFORM";
    /**
     * The file specified was not found on the given location
     */
    ErrorCode[ErrorCode["FILE_NOT_FOUND"] = 404] = "FILE_NOT_FOUND";
    /**
     * The return value is too big and has exceeded our size boundaries
     */
    ErrorCode[ErrorCode["SIZE_EXCEEDED"] = 10000] = "SIZE_EXCEEDED";
})(ErrorCode || (ErrorCode = {}));
/**
 * Indicates the team type, currently used to distinguish between different team
 * types in Office 365 for Education (team types 1, 2, 3, and 4).
 */
export var TeamType;
(function (TeamType) {
    TeamType[TeamType["Standard"] = 0] = "Standard";
    TeamType[TeamType["Edu"] = 1] = "Edu";
    TeamType[TeamType["Class"] = 2] = "Class";
    TeamType[TeamType["Plc"] = 3] = "Plc";
    TeamType[TeamType["Staff"] = 4] = "Staff";
})(TeamType || (TeamType = {}));
/**
 * The type of the channel with which the content is associated.
 */
export var ChannelType;
(function (ChannelType) {
    ChannelType["Regular"] = "Regular";
    ChannelType["Private"] = "Private";
    ChannelType["Shared"] = "Shared";
})(ChannelType || (ChannelType = {}));
/**
 * Indicates the various types of roles of a user in a team.
 */
export var UserTeamRole;
(function (UserTeamRole) {
    UserTeamRole[UserTeamRole["Admin"] = 0] = "Admin";
    UserTeamRole[UserTeamRole["User"] = 1] = "User";
    UserTeamRole[UserTeamRole["Guest"] = 2] = "Guest";
})(UserTeamRole || (UserTeamRole = {}));
/**
 * Allowed user file open preferences
 */
export var FileOpenPreference;
(function (FileOpenPreference) {
    FileOpenPreference["Inline"] = "inline";
    FileOpenPreference["Desktop"] = "desktop";
    FileOpenPreference["Web"] = "web";
})(FileOpenPreference || (FileOpenPreference = {}));
export var ActionObjectType;
(function (ActionObjectType) {
    ActionObjectType["M365Content"] = "m365content";
})(ActionObjectType || (ActionObjectType = {}));
/**
 * These correspond with field names in the MSGraph
 */
export var SecondaryM365ContentIdName;
(function (SecondaryM365ContentIdName) {
    SecondaryM365ContentIdName["DriveId"] = "driveId";
    SecondaryM365ContentIdName["GroupId"] = "groupId";
    SecondaryM365ContentIdName["SiteId"] = "siteId";
    SecondaryM365ContentIdName["UserId"] = "userId";
})(SecondaryM365ContentIdName || (SecondaryM365ContentIdName = {}));
/**
 * Full set of Data Residencies supported by Hub SDK (and OTel)
 */
export var DataResidency;
(function (DataResidency) {
    /**
     * Public
     */
    DataResidency["Public"] = "public";
    /**
     * European Union Data Boundary
     */
    DataResidency["EUDB"] = "eudb";
    /**
     * US Gov DOD
     */
    DataResidency["DOD"] = "dod";
    /**
     * US Gov DOJ
     */
    DataResidency["DOJ"] = "doj";
})(DataResidency || (DataResidency = {}));
/**
 * Limited set of Data Residencies information exposed to 1P application developers
 */
export var DataResidencySlim;
(function (DataResidencySlim) {
    /**
     * Public
     */
    DataResidencySlim["Public"] = "public";
    /**
     * European Union Data Boundary
     */
    DataResidencySlim["EUDB"] = "eudb";
    /**
     * Other, stored to cover DoD and DoJ fields that will not be exposed
     */
    DataResidencySlim["Other"] = "other";
})(DataResidencySlim || (DataResidencySlim = {}));
//# sourceMappingURL=hosted-web-content-sdk.interface.js.map